import {
  LogoInnovamat,
  RegisterBg,
  UEFlag,
  WavesBottomBg,
  WavesTopBg,
} from '@innovamat/glimmer-assets';

import {
  Dropdown,
  GridCol,
  GridHidden,
  GridRow,
  Icon,
  Icon as IconGlimmer,
  ImageComponent,
} from '@innovamat/glimmer-components';
import { useQueryParams } from '@innovamat/hooks';
import {
  addParamToUrl,
  addParamToUrlAndRefresh,
} from '@innovamat/radiance-utils';
import { useRegionalConfigVariables } from '@innovamat/regional-config-variables';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import styled from '@emotion/styled';
import { useEnvVariables } from '../../../env-variables-manager/env-variables-provider';
import { useLanguage } from '../../hooks/use-language';
import LanguageButton from './language-button';
import {
  CountryRegionButtonWrapper,
  Description,
  DropdownSelection,
  ErrorContainer,
  FromDataCenter,
  FromText,
  GoBackLink,
  GridColWithBg,
  GridContainerStyled,
  Header,
  LogoContainer,
  MainGridRow,
  RegisterImage,
  StyledNotification,
  Title,
  WavesBottom,
  WavesTop,
} from './login-register-layout.styles';

export type NotificationType =
  | {
      message: string | JSX.Element;
      type: 'error' | 'warning';
    }
  | undefined;

type Props = {
  isRegisterLayout?: boolean;
  isSuccess?: boolean;
  title?: string;
  description?: string;
  children: JSX.Element;
  error?: string | JSX.Element;
  canGoBack?: boolean;
  onSuccessBody?: JSX.Element;
  notification?: NotificationType;
  handleIsUSA?: (isUSA: boolean) => void;
  hasLanguageDropdown?: boolean;
};

function LoginRegisterLayout({
  isRegisterLayout,
  isSuccess,
  children,
  title,
  description,
  canGoBack,
  onSuccessBody,
  error,
  notification,
  handleIsUSA,
  hasLanguageDropdown,
}: Props): JSX.Element {
  const { t, i18n } = useTranslation();
  const params = useQueryParams();
  const { ENVIRONMENT } = useEnvVariables();
  const { setHtmlLanguage } = useLanguage();
  const header = document.getElementById('headerLoginLayout');

  const { lng } = useParams<{ lng: string }>();
  const selectedLanguage =
    params.get('l') || params.get('language') || lng || i18n.language;

  const {
    geoLocation: { isUS, country },
  } = useRegionalConfigVariables();
  const dataCenter =
    params.get('dc')?.toLowerCase() === 'usa'
      ? 'USA'
      : params.get('dc')?.toLowerCase() === 'europe'
      ? 'Europe'
      : params.get('dc')?.toLowerCase() === 'latam'
      ? 'LATAM'
      : params.get('dc');

  const [isPressed, setIsPressed] = useState(false);
  const [dataCenterRegion, setDataCenterRegion] = useState(dataCenter);
  handleIsUSA?.(dataCenterRegion === 'USA');

  const setDataCenterRegionAndChangeUrl = (
    region: string,
    language?: string
  ) => {
    addParamToUrl('dc', region);
    addParamToUrlAndRefresh('l', language || selectedLanguage);
    setDataCenterRegion(region);
  };

  const handleSelectLanguage = async (languageLocale: string) => {
    addParamToUrl('l', languageLocale);
    await i18n.changeLanguage(languageLocale);
  };

  useEffect(() => {
    handleSelectLanguage(selectedLanguage);
    setHtmlLanguage(selectedLanguage || 'es');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    if (!dataCenter) {
      if (isUS) {
        setDataCenterRegion('USA');
        handleSelectLanguage('en');
        handleIsUSA?.(true);
      } else if (
        country.toLocaleUpperCase().includes('ES') ||
        country.toLocaleUpperCase().includes('IT')
      ) {
        setDataCenterRegion('Europe');
      } else {
        setDataCenterRegion('LATAM');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showFlag = ENVIRONMENT === 'staging';

  return (
    <>
      <GridContainerStyled>
        <GridRow noGutters>
          <GridCol xs={12} sm={12} md={8} lg={8}>
            <GridRow id="headerLoginLayout" noGutters>
              <GridCol>
                <Header>
                  <LogoContainer>
                    <Link to="/">
                      <LogoInnovamat />
                    </Link>
                  </LogoContainer>
                  {hasLanguageDropdown && (
                    <CountryRegionButtonWrapper>
                      <LanguageButton
                        language={t(`language.${i18n.language}`)}
                        handleLanguageClick={handleSelectLanguage}
                      />
                    </CountryRegionButtonWrapper>
                  )}
                </Header>
              </GridCol>
            </GridRow>
            <MainGridRow
              justify="center"
              headerHeight={header?.clientHeight || 0}
              noGutters
            >
              <GridCol xs={12} sm={12} md={6} lg={6}>
                {isSuccess && onSuccessBody ? (
                  onSuccessBody
                ) : (
                  <>
                    {notification && (
                      <StyledNotification
                        type={notification.type}
                        canClose={false}
                        text={notification.message}
                        data-testid="login-register-notification"
                      />
                    )}
                    {canGoBack && (
                      <GoBackLink to="/">
                        <Icon icon="BackIcon" /> {t('back.button')}
                      </GoBackLink>
                    )}

                    <>
                      {title && <Title>{title}</Title>}
                      {description && <Description>{description}</Description>}
                      <FromDataCenter descriptionExists={!!description}>
                        <FromText>
                          {t('login.datacenter.from', 'Desde')}:
                        </FromText>
                        <Dropdown closeOnSelectItem onToggle={setIsPressed}>
                          <Dropdown.Toggle dataTestId="data-center-selector">
                            <DropdownSelection isPressed={isPressed}>
                              <IconGlimmer size="S" icon="ArrangeIcon" />
                              {dataCenterRegion === 'USA' && (
                                <>
                                  <IconGlimmer size="L" icon="UsaIcon" />
                                  {t('login.datacenter.region.usa', 'EEUU')}
                                </>
                              )}
                              {dataCenterRegion === 'Europe' && (
                                <>
                                  <IconGlimmer size="L" icon="EuropeIcon" />
                                  {t(
                                    'login.datacenter.region.europe',
                                    'Europe'
                                  )}
                                </>
                              )}
                              {dataCenterRegion === 'LATAM' && (
                                <>
                                  <IconGlimmer size="L" icon="LatamIcon" />
                                  {t('login.datacenter.region.latam', 'LATAM')}
                                </>
                              )}
                            </DropdownSelection>
                          </Dropdown.Toggle>
                          <Dropdown.Content position="bottomLeft">
                            <Dropdown.Item
                              state="active"
                              icon="UsaIcon"
                              dataTestId="data-center-option-usa"
                              onSelectItem={() => {
                                setDataCenterRegionAndChangeUrl('USA', 'en');
                              }}
                            >
                              {t('login.datacenter.region.usa', 'EEUU')}
                            </Dropdown.Item>
                            <Dropdown.Item
                              state="active"
                              icon="EuropeIcon"
                              dataTestId="data-center-option-europe"
                              onSelectItem={() => {
                                setDataCenterRegionAndChangeUrl('Europe');
                              }}
                            >
                              {t('login.datacenter.region.europe', 'Europe')}
                            </Dropdown.Item>
                            <Dropdown.Item
                              state="active"
                              icon="LatamIcon"
                              dataTestId="data-center-option-latam"
                              onSelectItem={() => {
                                setDataCenterRegionAndChangeUrl('LATAM');
                              }}
                            >
                              {t('login.datacenter.region.latam', 'LATAM')}
                            </Dropdown.Item>
                          </Dropdown.Content>
                        </Dropdown>
                      </FromDataCenter>
                      <ErrorContainer>{error}</ErrorContainer>
                    </>
                    {children}
                  </>
                )}
              </GridCol>
            </MainGridRow>
          </GridCol>
          <GridHidden xs sm>
            <GridColWithBg
              isRegisterLayout={isRegisterLayout}
              xs={12}
              sm={12}
              md={4}
              lg={4}
            >
              {showFlag && <FlagLogo src={(UEFlag as any).src} alt="flag" />}

              {isRegisterLayout ? (
                <RegisterImage bg={(RegisterBg as any).src} />
              ) : (
                <>
                  <WavesTop>
                    <WavesTopBg width="100%" />
                  </WavesTop>
                  <WavesBottom>
                    <WavesBottomBg width="100%" />
                  </WavesBottom>
                </>
              )}
            </GridColWithBg>
          </GridHidden>
        </GridRow>
      </GridContainerStyled>
    </>
  );
}

const FlagLogo = styled(ImageComponent)`
  position: absolute;
  width: 323px;
  margin-left: 8px;
  z-index: 1;
  top: 8px;
`;

export default LoginRegisterLayout;
